import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";

import {
     lcvReportsClearGamesExport,
     lcvReportsGetFilterOptionsRequest,
     lcvReportsGetGamesExportRequest,
     lcvReportsGetGamesRequest,
} from "../../../../../_redux/_actions/lcvReports.action";
// Imported from their own file rather than the _constants barrel: the barrel can still be
// half-initialised when this module evaluates, which leaves the constants undefined.
import { lcvReportsConstants } from "../../../../../_redux/_constants/lcvReports.constants";
import ExportToExcelWithApi from "../../../../_uiComponents/common/ExportToExcelWithApi";
import useLcvCampaign from "../../shared/useLcvCampaign";

const ALL_OPTION = { id: null, name: "All" };

// A code the dealership still holds, one somebody holds but has not used, and one that has been
// played are three different problems for whoever reads the report.
const CODE_STATE_OPTIONS = [
     { id: null, name: "All" },
     { id: "unallocated", name: "Unallocated" },
     { id: "allocated", name: "Allocated" },
     { id: "played", name: "Played" },
];

const INITIAL_FILTERS = {
     area: ALL_OPTION,
     dealer: ALL_OPTION,
     jobRole: ALL_OPTION,
     codeState: CODE_STATE_OPTIONS[0],
};

const INITIAL_PAGE = { skip: 0, take: 25 };

const INITIAL_OPTION_SEARCH = { dealerSearch: "", areaSearch: "", jobRoleSearch: "" };

const EXPORT_FILE_NAME = "lcv-game-report";

const LcvGameReportGrid = () => {
     const actionDispatch = useDispatch();

     const gamePage = useSelector((state) => state?.lcvReports?.gamePage);
     const gameExport = useSelector((state) => state?.lcvReports?.gameExport);
     const filterOptions = useSelector((state) => state?.lcvReports?.filterOptions);

     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               lcvReportsConstants.LCV_REPORTS_GET_GAMES_REQUEST,
          ),
     );

     const { selectedCampaignId } = useLcvCampaign();

     const [filters, setFilters] = useState(INITIAL_FILTERS);
     const [search, setSearch] = useState("");
     const [page, setPage] = useState(INITIAL_PAGE);
     const [sort, setSort] = useState([]);
     const [optionSearch, setOptionSearch] = useState(INITIAL_OPTION_SEARCH);

     // The dropdowns are searchable and the typed text is matched by the backend, so the option
     // lists are re-fetched rather than narrowed here.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    lcvReportsGetFilterOptionsRequest({
                         dealerSearch: optionSearch.dealerSearch || undefined,
                         areaSearch: optionSearch.areaSearch || undefined,
                         jobRoleSearch: optionSearch.jobRoleSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, optionSearch]);

     // Filtering, sorting and paging are all done by the backend: the request carries the criteria
     // and the response is exactly the page that should be shown.
     useEffect(() => {
          if (!selectedCampaignId) {
               return undefined;
          }

          const timer = setTimeout(() => {
               actionDispatch(lcvReportsGetGamesRequest(criteria(filters, search, sort, page)));
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, filters, search, sort, page, selectedCampaignId]);

     const areaOptions = useMemo(
          () => [ALL_OPTION, ...(filterOptions?.areas || [])],
          [filterOptions],
     );

     const dealerOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(filterOptions?.dealers || []).map((dealer) => ({
                    id: dealer.id,
                    name: `${dealer.dealerCode} - ${dealer.name}`,
               })),
          ],
          [filterOptions],
     );

     const jobRoleOptions = useMemo(
          () => [ALL_OPTION, ...(filterOptions?.jobRoles || [])],
          [filterOptions],
     );

     // A code that has not been allocated keeps its row with the person cells empty, so the dates
     // are only rendered when they carry a value.
     const gameCodes = useMemo(() => {
          const list = gamePage?.gameCodes || [];

          return list.map((row) => ({
               ...row,
               name: `${row.firstName || ""} ${row.lastName || ""}`.trim(),
               allocatedOnDisplay: row.allocatedOn
                    ? moment(row.allocatedOn).format("DD/MM/YYYY")
                    : "",
               playedOnDisplay: row.playedOn ? moment(row.playedOn).format("DD/MM/YYYY") : "",
          }));
     }, [gamePage]);

     const totalCount = gamePage?.totalCount || 0;

     const onFilterChange = useCallback((field, value) => {
          setFilters((previous) => ({ ...previous, [field]: value }));
          setPage(INITIAL_PAGE);
     }, []);

     const onSearchChange = useCallback((event) => {
          setSearch(event.value);
          setPage(INITIAL_PAGE);
     }, []);

     const onOptionSearchChange = useCallback((field, event) => {
          setOptionSearch((previous) => ({
               ...previous,
               [field]: event.filter ? event.filter.value : "",
          }));
     }, []);

     const clearFilters = useCallback(() => {
          setFilters(INITIAL_FILTERS);
          setSearch("");
          setSort([]);
          setPage(INITIAL_PAGE);
          setOptionSearch(INITIAL_OPTION_SEARCH);
     }, []);

     // The file holds every row the filters match, not the page on screen, so the export request
     // carries the same criteria without the paging ones.
     const fetchForExport = useCallback(
          (onDownloadComplete) => {
               try {
                    actionDispatch(lcvReportsClearGamesExport());
                    actionDispatch(
                         lcvReportsGetGamesExportRequest(criteria(filters, search, sort)),
                    );
               } catch (error) {
                    onDownloadComplete(false);
               }
          },
          [actionDispatch, filters, search, sort],
     );

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "260px" }}
                         value={search}
                         onChange={onSearchChange}
                         placeholder="Search name, email or code"
                    />
                    <DropDownList
                         style={{ width: "150px" }}
                         label="Area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.area}
                         filterable={true}
                         onFilterChange={(event) => onOptionSearchChange("areaSearch", event)}
                         onChange={(event) => onFilterChange("area", event.value)}
                    />
                    <DropDownList
                         style={{ width: "280px" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.dealer}
                         filterable={true}
                         onFilterChange={(event) => onOptionSearchChange("dealerSearch", event)}
                         onChange={(event) => onFilterChange("dealer", event.value)}
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Role"
                         data={jobRoleOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.jobRole}
                         filterable={true}
                         onFilterChange={(event) => onOptionSearchChange("jobRoleSearch", event)}
                         onChange={(event) => onFilterChange("jobRole", event.value)}
                    />
                    <DropDownList
                         style={{ width: "160px" }}
                         label="Code state"
                         data={CODE_STATE_OPTIONS}
                         textField="name"
                         dataItemKey="name"
                         value={filters.codeState}
                         onChange={(event) => onFilterChange("codeState", event.value)}
                    />
                    <Button type="button" onClick={clearFilters}>
                         Clear
                    </Button>
                    <ExportToExcelWithApi
                         name={EXPORT_FILE_NAME}
                         data={gameExport}
                         onDownloadClick={fetchForExport}
                    />
               </div>
               <div style={{ position: "relative" }}>
                    {isLoading && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}
                    <Grid
                         data={gameCodes}
                         skip={page.skip}
                         take={page.take}
                         total={totalCount}
                         sort={sort}
                         sortable={true}
                         className="sba-area-data-grid"
                         pageable={{
                              buttonCount: 4,
                              pageSizes: [10, 25, 50],
                         }}
                         onPageChange={(event) => setPage(event.page)}
                         onSortChange={(event) => {
                              setSort(event.sort);
                              setPage(INITIAL_PAGE);
                         }}
                    >
                         <GridColumn field="gameCode" title="Game Code" />
                         <GridColumn field="area" title="Area" />
                         <GridColumn field="dealerCode" title="Dealer Code" />
                         <GridColumn field="dealerName" title="Dealer" />
                         <GridColumn field="jobRole" title="Role" />
                         <GridColumn field="name" title="Name" sortable={false} />
                         <GridColumn field="email" title="Email" />
                         <GridColumn
                              field="allocatedOnDisplay"
                              title="Allocated"
                              sortable={false}
                         />
                         <GridColumn
                              field="playedOnDisplay"
                              title="Played"
                              sortable={false}
                         />
                         <GridColumn field="rewardEarned" title="Reward" />
                    </Grid>
               </div>
          </div>
     );
};

// Paging is left out when no page is given, which is what the export route wants.
const criteria = (filters, search, sort, page) => {
     const payload = {
          areaId: filters.area.id,
          dealershipId: filters.dealer.id,
          jobRoleId: filters.jobRole.id,
          codeState: filters.codeState.id || undefined,
          searchTerm: search.trim() ? search.trim() : undefined,
          sortBy: sort.length ? sort[0].field : undefined,
          sortDirection: sort.length ? sort[0].dir : undefined,
     };

     if (!page) {
          return payload;
     }

     return {
          ...payload,
          page: Math.floor(page.skip / page.take) + 1,
          pageSize: page.take,
     };
};

export default LcvGameReportGrid;
